/**
 * @file billCostCheck.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description Add Bill: a bill line whose cost differs from what the order
 *              was costed at, or from the customer's contract, needs its cost
 *              CHOSEN — never accepted by default (Cam, 28 Oct 2026: "if the
 *              bill has a different cost price to the order (where order is
 *              not 0) or the order had (or now has) a contract price for that
 *              customer / item / period / delivery date combo and the cost on
 *              the contract differs to the bill or order from the contract
 *              cost price (where not 0), can we make the cost field mandatory
 *              and not chosen by default please? Most times user would want to
 *              query that bill, but sometimes they will accept").
 *
 *   ONE RULE, BOTH SIDES. Add Bill (client) flags the line, leaves its cost
 *   unchosen and greys Save until a cost is chosen or the bill is queried;
 *   the bill save (services/billCostCheckService) refuses a bill whose
 *   flagged line carries no choice (409 COST_CHOICE_REQUIRED).
 *
 *   ALL IN THE PO LINE'S UNIT. The bill's unit cost is read in its PO line's
 *   unit (utils/billUnitConversion — a bill per tonne against bags is £10 a
 *   bag, not £400); the PO line's cost and the order line's (its cost_price
 *   plus the PO line's additives — the PO-line basis, as the cost write-back
 *   compares them) are in it already; the contract's cost is per the item's
 *   unit and comes converted (the server, utils/itemCostUnit).
 *
 *   A DISCREPANCY (costCheck), at 4 dp (half a hundredth of a penny — the
 *   tolerance Add Bill's cells and the cost write-back compare at):
 *     bill_po        the bill's cost ≠ the PO line's cost (not 0);
 *     bill_order     the bill's cost ≠ the order line's cost (not 0);
 *     contract_bill  the contract's cost (not 0) ≠ the bill's;
 *     contract_order the contract's cost (not 0) ≠ the order's (PO or order line, not 0).
 *   Which contract: the one Pricing & Costing finds for the order line
 *   (pricingCostingService.contractFor — linked, its PO line's contract
 *   number, else one in force on the delivery date; on hold and archived
 *   included, drafts never), so the two screens agree.
 *
 *   THE CHOICE. `line_choices[].choices.price` — which figure the bill line
 *   took: 'bill' (the invoice's), 'po' (the PO line's), 'order' (the order
 *   line's), 'contract', 'typed'. Recorded on the bill's CREATE / UPDATE row
 *   with the other choices (`_line_choices`). A Queried bill needs none: the
 *   line keeps the cost as invoiced and no PO cost changes.
 *
 *   Pure.
 */

import { roundHalfUp } from './numberFormatters';
import { billGroupFigures, billUnitConversion } from './billUnitConversion';
import { unitLabelFor } from './units';

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(String(v).replace(/[£,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};
const r4 = (n) => roundHalfUp(n, 4);

/** Which figure a flagged line's cost was taken from. */
export const PRICE_CHOICES = Object.freeze(['bill', 'po', 'order', 'contract', 'typed']);

/** Two costs that agree to 4 dp are the same cost. */
export const COST_TOLERANCE = 0.00005;

/** Do two costs differ (both known)? */
export function costsDiffer(a, b) {
  const x = num(a);
  const y = num(b);
  if (x === null || y === null) return false;
  return Math.abs(r4(x) - r4(y)) >= COST_TOLERANCE;
}

/** A cost that counts: above 0 (0 means "not set"), at 4 dp; else null. */
export function setCost(v) {
  const n = num(v);
  return n !== null && n > 0 ? r4(n) : null;
}

/** `${poId}|${poLineKey}` — how a PO line is named in the check. */
export const checkKey = (poId, key) => `${Number(poId)}|${String(key)}`;

/**
 * The bill's unit cost for one PO line, in the PO line's unit: its lines
 * grouped as the cost write-back groups them (utils/billUnitConversion
 * .billGroupFigures), read per the PO line's unit when billed in another.
 *
 * @param {object} p
 * @param {Array<{ quantity, unit_amount, line_amount?, unit? }>} p.lines — the bill's lines on this PO line
 * @param {object} p.li — the PO's line_items element (quantity, unitAmount, unitWeightKg, description)
 * @param {number|null} [p.supplied] — supplied quantity recorded on it (none: null)
 * @param {number|null} [p.itemWeight] — its item's unit weight
 * @returns {{ cost: number, asBilled: number, conversion: object|null }|null}
 */
export function billCostInPoUnit({ lines, li, supplied = null, itemWeight = null } = {}) {
  const g = billGroupFigures(lines || []);
  if (g.cost === null || !(g.cost > 0)) return null;
  const conversion = billUnitConversion({
    billQty: g.qty, billCost: g.cost, billAmount: g.amount, billUnit: g.unit ?? null, poCost: li?.unitAmount,
    poQty: [num(li?.quantity) || 0, supplied === undefined ? null : supplied],
    lineWeight: li?.unitWeightKg, itemWeight, poWords: li?.description || null,
  });
  const cost = conversion ? r4(conversion.cost) : g.cost;
  if (!(cost > 0)) return null;
  return { cost, asBilled: g.cost, conversion: conversion || null };
}

/**
 * Does this bill line's cost need choosing?
 *
 * @param {object} p — every figure per the PO line's unit
 * @param {number|null} p.bill — the bill's unit cost
 * @param {number|null} [p.po] — the PO line's unit cost
 * @param {number|null} [p.order] — the order line's cost on the PO-line basis (cost_price + additives)
 * @param {object|null} [p.contract] — { cost, ref, detail, kind, id, … } (cost per the PO line's unit)
 * @returns {{ bill: number, po: number|null, order: number|null, contract: object|null, reasons: string[] }|null}
 */
export function costCheck({ bill, po = null, order = null, contract = null } = {}) {
  const b = setCost(bill);
  if (b === null) return null;
  const p = setCost(po);
  const o = setCost(order);
  const cc = contract && setCost(contract.cost) !== null ? { ...contract, cost: setCost(contract.cost) } : null;
  const reasons = [];
  if (p !== null && costsDiffer(b, p)) reasons.push('bill_po');
  if (o !== null && costsDiffer(b, o)) reasons.push('bill_order');
  if (cc) {
    if (costsDiffer(cc.cost, b)) reasons.push('contract_bill');
    if ((o !== null && costsDiffer(cc.cost, o)) || (p !== null && costsDiffer(cc.cost, p))) reasons.push('contract_order');
  }
  if (!reasons.length) return null;
  return { bill: b, po: p, order: o, contract: cc, reasons };
}

/** "£400.00" — 2 to 4 dp, as a cost. */
export const costMoney = (v) => `£${(r4(num(v) ?? 0) ?? 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;

/** "/T", "/25kg bag" — or '' when the PO line's unit is not known. */
export function perUnitText(kg) {
  const label = unitLabelFor(kg);
  return label ? `/${label}` : '';
}

/** "DD/MM/YYYY" from "YYYY-MM-DD…". */
export function ukDay(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** "contract EXA123" / "the contract" — how a contract is named on the line. */
export function contractName(contract) {
  if (!contract) return 'the contract';
  const ref = String(contract.ref || '').trim();
  if (contract.kind === 'monthly') return ref ? `straights month ${ref}` : 'the straights month';
  return ref ? `contract ${ref}` : 'the contract';
}

/**
 * The figures in one line: "Bill £410.00/T · order cost £400.00/T (PO-D10123,
 * SO-D10120) · contract EXA123 cost £395.00/T (Winter 2026-27; in force on
 * 12/10/2026)".
 *
 * @param {ReturnType<typeof costCheck>} check
 * @param {{ kg?: number|null, poNumber?: string|null, orderNumber?: string|null, deliveryDate?: string|null, billAsBilled?: string|null }} [o]
 */
export function costCheckText(check, { kg = null, poNumber = null, orderNumber = null, deliveryDate = null, billAsBilled = null } = {}) {
  if (!check) return '';
  const per = perUnitText(kg);
  const parts = [`Bill ${costMoney(check.bill)}${per}${billAsBilled ? ` (${billAsBilled})` : ''}`];
  if (check.po !== null && check.order !== null && !costsDiffer(check.po, check.order)) {
    const where = [poNumber, orderNumber].filter(Boolean).join(', ');
    parts.push(`order cost ${costMoney(check.order)}${per}${where ? ` (${where})` : ''}`);
  } else {
    if (check.po !== null) parts.push(`${poNumber || 'PO'} cost ${costMoney(check.po)}${per}`);
    if (check.order !== null) parts.push(`${orderNumber ? `${orderNumber} ` : 'order '}cost ${costMoney(check.order)}${per}`);
  }
  if (check.contract) {
    const c = check.contract;
    const when = [c.detail, deliveryDate ? `in force on ${ukDay(deliveryDate)}` : ''].filter(Boolean).join('; ');
    parts.push(`${contractName(c)} cost ${costMoney(c.cost)}${per}${when ? ` (${when})` : ''}`);
  }
  return parts.join(' · ');
}

/** Why it matters, in a sentence. */
export function costCheckWhy(check) {
  if (!check) return '';
  const r = new Set(check.reasons);
  const offOrder = r.has('bill_po') || r.has('bill_order');
  let said;
  if (offOrder && (r.has('contract_bill') || r.has('contract_order'))) said = "The bill's cost is not what the order was costed at, and the contract's cost is different again.";
  else if (offOrder) said = "The bill's cost is not what the order was costed at.";
  else if (r.has('contract_bill') && r.has('contract_order')) said = "The contract's cost is not what the bill or the order says.";
  else if (r.has('contract_bill')) said = "The bill's cost is not the contract's.";
  else said = "The contract's cost is not what the order was costed at.";
  return `${said} Choose the cost this line takes — or query the bill with the supplier.`;
}

/** The request's price choices: `${poId}|${key}` → 'bill' | 'po' | 'order' | 'contract' | 'typed'. */
export function priceChoicesOf(lineChoices) {
  const out = new Map();
  for (const e of Array.isArray(lineChoices) ? lineChoices : []) {
    const poId = parseInt(e?.purchase_order_id, 10);
    const key = e?.po_line_key ? String(e.po_line_key) : null;
    const v = e?.choices?.price;
    if (Number.isInteger(poId) && key && PRICE_CHOICES.includes(v)) out.set(checkKey(poId, key), v);
  }
  return out;
}

/** "line 2", "lines 2 and 4", "lines 1, 3 and 5". */
export function linesText(numbers) {
  const list = [...new Set((numbers || []).filter(n => Number.isInteger(n) && n > 0))].sort((a, b) => a - b);
  if (!list.length) return 'a line';
  if (list.length === 1) return `line ${list[0]}`;
  return `lines ${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/** Save's reason while a flagged line has no cost chosen. */
export function costChoiceRequiredText(lineNumbers) {
  return `Choose a cost for ${linesText(lineNumbers)} — the bill, order and contract costs differ — or query the bill.`;
}
